import { Box3, Vector2, Vector2Tuple, Vector3 } from 'three';

import {
  pickSchemaFields,
  Schema,
  SchemaFieldFurniture,
  SchemaFieldNumber,
  SchemaFields,
  SchemaFieldTypesMapping,
  SchemaValues,
} from '../schema/index.js';
import { FInstanceNode } from '../types/index.js';

import { FEnsembleBase } from './FEnsembleBase.js';

const bbox = new Box3();
const vec3 = new Vector3();

export interface FEnsembleDesktopSchemaFields extends SchemaFields {
  chairType: SchemaFieldFurniture;
  tableType: SchemaFieldFurniture;
  chairsPerTable: SchemaFieldNumber;
  chairCenterOffset: SchemaFieldNumber;
  chairsGap: SchemaFieldNumber;
  chairDistanceFromTable: SchemaFieldNumber;
  tableOffset: SchemaFieldNumber;
}

export interface FEnsembleDesktopSchema extends Schema {
  fields: FEnsembleDesktopSchemaFields;
}

export interface FEnsembleDesktopValues {
  tableSize: SchemaFieldTypesMapping['vec2'];
  chairSize: SchemaFieldTypesMapping['vec2'];
}

export type FEnsembleDesktopAttributes = Partial<SchemaValues<FEnsembleDesktopSchemaFields>> &
  FEnsembleDesktopValues;

export class FEnsembleDesktop extends FEnsembleBase<
  FEnsembleDesktopSchema,
  FEnsembleDesktopValues
> {
  constructor(schema: FEnsembleDesktopSchema, initialValues?: Partial<FEnsembleDesktopAttributes>) {
    super(
      {
        ...schema,
        fields: pickSchemaFields(schema, [
          'chairType',
          'tableType',
          'chairsPerTable',
          'chairCenterOffset',
          'chairsGap',
          'chairDistanceFromTable',
          'tableOffset',
        ]) as any,
      },
      {
        tableSize: [0, 0],
        chairSize: [0, 0],
        ...initialValues,
      }
    );
    this.setValue('getMaxTableOffset', this.getMaxTableOffset);
  }

  getMaxTableOffset(tableWidth: number, chairWidth: number) {
    return Math.min((tableWidth - chairWidth) * 0.5, 1);
  }

  getNodeTypes() {
    const { chairType, tableType } = this.getValues();

    if (!chairType) {
      throw new Error('chairType is required');
    }

    if (!tableType) {
      throw new Error('tableType is required');
    }

    return [chairType, tableType];
  }

  updateObjectMeasurements() {
    const [chair, table] = this.getObjects();

    if (!chair) {
      throw new Error('chair table object');
    }

    if (!table) {
      throw new Error('missing table object');
    }

    const tableBoundingBox = bbox.setFromObject(table);
    tableBoundingBox.getSize(vec3);
    const tableSize = new Vector2(vec3.x, vec3.z);

    const chairBoundingBox = bbox.setFromObject(chair);
    chairBoundingBox.getSize(vec3);
    const chairSize = new Vector2(vec3.x, vec3.z);

    this.updateValues({
      tableSize: tableSize.toArray(),
      chairSize: chairSize.toArray(),
    });
  }

  computeNodes(): FInstanceNode[] {
    if (this.getObjects().length === 0) {
      return [];
    }

    const {
      chairType,
      tableType,
      chairsPerTable,
      chairCenterOffset,
      chairsGap,
      chairDistanceFromTable,
      tableSize,
      chairSize,
      tableOffset,
    } = this.getValues();

    const nodes: FInstanceNode[] = [];

    const availableTableLength = tableSize[0] - tableOffset * 2;
    const center = chairCenterOffset * availableTableLength;

    const startX =
      chairCenterOffset <= 0
        ? Math.max(
            center - chairsPerTable * 0.5 * (chairSize[0] + chairsGap), // left side start point needed based on center offset
            -availableTableLength * 0.5 // limit set by offset
          )
        : Math.min(
            center + chairsPerTable * 0.5 * (chairSize[0] + chairsGap), // right side end point needed based on center offset
            availableTableLength * 0.5 // limit set by offset
          ) -
          chairsPerTable * (chairSize[0] + chairsGap); // translate to left side start point

    const elementSizeY =
      tableSize[1] + chairSize[1] * 0.5 * chairDistanceFromTable + chairSize[1] * 0.5;
    const tPos: Vector2Tuple = [0, -elementSizeY / 2 + tableSize[1] / 2];
    const startY = tPos[1] + tableSize[1] * 0.5 + chairDistanceFromTable * chairSize[1] * 0.5;

    nodes.push({
      type: 'table',
      id: tableType,
      position: tPos,
      rotation: 0,
    });

    for (let i = 0; i < this.getSafeIterations(chairsPerTable); i++) {
      const chairPosition = [
        startX + chairsGap * 0.5 + i * (chairSize[0] + chairsGap) + chairSize[0] * 0.5,
        startY,
      ] as Vector2Tuple;

      nodes.push({
        type: 'chair',
        id: chairType,
        position: chairPosition,
        rotation: 0,
      });
    }

    return nodes;
  }
}
